import { useCallback, useEffect, useMemo, useState } from 'react';
import type { KitPatternSlot, KitSupportedCardCount, KitTemplate } from '../shared/types';
import {
  KIT_TEMPLATE_CARD_SCALE_MAX,
  KIT_TEMPLATE_CARD_SCALE_MIN,
  KIT_TEMPLATE_CARD_SCALE_STEP,
  resolveKitTemplateSlots,
  selectKitStitchingPattern,
} from '../shared/types';
import { useKitTemplatesApi, uploadKitTemplateBackgroundToS3 } from './kit-templates-api';
import {
  kitTemplateBackgroundLabel,
  kitTemplateBackgroundColor,
  kitTemplateBackgroundPreviewUrl,
  kitTemplateCardScaleLabel,
  kitTemplateStatusLabel,
} from './kit-templates-ui';

function failureMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Unable to load Kit templates';
}

export function KitTemplatesPage(): JSX.Element {
  const templatesApi = useKitTemplatesApi();
  const [templates, setTemplates] = useState<readonly KitTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [templateName, setTemplateName] = useState('');
  const [cardCount, setCardCount] = useState<KitSupportedCardCount>(5);
  const [cardScale, setCardScale] = useState(0.9);
  const [backgroundFile, setBackgroundFile] = useState<File | undefined>();
  const [backgroundPreviewUrl, setBackgroundPreviewUrl] = useState<string | undefined>();
  const [createError, setCreateError] = useState<string | undefined>();
  const [creating, setCreating] = useState(false);
  const [activatingTemplateId, setActivatingTemplateId] = useState<string | undefined>();
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | undefined>();
  const [backgroundPopoutTemplate, setBackgroundPopoutTemplate] = useState<
    KitTemplate | undefined
  >();

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const res = await templatesApi.list();
      if (!res.ok) throw new Error(res.error);
      setTemplates(res.data.templates);
    } catch (err) {
      setError(failureMessage(err));
    } finally {
      setLoading(false);
    }
  }, [templatesApi]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (backgroundFile === undefined) {
      setBackgroundPreviewUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(backgroundFile);
    setBackgroundPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [backgroundFile]);

  useEffect(() => {
    if (backgroundPopoutTemplate === undefined) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setBackgroundPopoutTemplate(undefined);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [backgroundPopoutTemplate]);

  const previewPattern = useMemo(() => selectKitStitchingPattern(cardCount), [cardCount]);
  const previewSlots = useMemo(
    () => (previewPattern === undefined ? [] : resolveKitTemplateSlots(previewPattern, cardScale)),
    [cardScale, previewPattern],
  );

  const createTemplate = async (): Promise<void> => {
    if (backgroundFile === undefined) {
      setCreateError('Choose a background image.');
      return;
    }
    const name = templateName.trim();
    if (name.length === 0) {
      setCreateError('Enter a template name.');
      return;
    }
    if (backgroundFile.type !== 'image/png' && backgroundFile.type !== 'image/jpeg') {
      setCreateError('Background must be a PNG or JPEG image.');
      return;
    }

    setCreating(true);
    setCreateError(undefined);
    try {
      const dimensions = await readImageDimensions(backgroundFile);
      const uploadUrl = await templatesApi.uploadBackgroundUrl({
        contentType: backgroundFile.type,
        sizeBytes: backgroundFile.size,
      });
      if (!uploadUrl.ok) throw new Error(uploadUrl.error);
      const upload = await uploadKitTemplateBackgroundToS3(uploadUrl.data, backgroundFile);
      if (!upload.ok) throw new Error(upload.error);
      const created = await templatesApi.create({
        name,
        cardCount,
        cardScale,
        background: {
          kind: 'image',
          s3Key: uploadUrl.data.key,
          contentType: backgroundFile.type,
          width: dimensions.width,
          height: dimensions.height,
        },
      });
      if (!created.ok) throw new Error(created.error);
      setTemplateName('');
      setBackgroundFile(undefined);
      setCardScale(0.9);
      await loadTemplates();
    } catch (err) {
      setCreateError(failureMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const activateTemplate = async (template: KitTemplate): Promise<void> => {
    setActivatingTemplateId(template.templateId);
    setError(undefined);
    try {
      const res = await templatesApi.activate(template.templateId);
      if (!res.ok) throw new Error(res.error);
      await loadTemplates();
    } catch (err) {
      setError(failureMessage(err));
    } finally {
      setActivatingTemplateId(undefined);
    }
  };

  const deleteTemplate = async (template: KitTemplate): Promise<void> => {
    if (
      !window.confirm(
        `Delete template "${template.name}"? Existing Kit images will be preserved, but this template will be hidden from template selection.`,
      )
    ) {
      return;
    }
    setDeletingTemplateId(template.templateId);
    setError(undefined);
    try {
      const res = await templatesApi.delete(template.templateId);
      if (!res.ok) throw new Error(res.error);
      await loadTemplates();
    } catch (err) {
      setError(failureMessage(err));
    } finally {
      setDeletingTemplateId(undefined);
    }
  };

  return (
    <div>
      <h2>Templates</h2>
      <section className="tcvs-card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>New Template</h3>
        {createError !== undefined ? (
          <div className="tcvs-alert tcvs-alert-danger">{createError}</div>
        ) : null}
        <div className="tcvs-form-grid">
          <label>
            <span>Name</span>
            <input
              value={templateName}
              onChange={(event) => setTemplateName(event.target.value)}
              placeholder="5-card blue backdrop"
            />
          </label>
          <label>
            <span>Cards</span>
            <select
              value={cardCount}
              onChange={(event) =>
                setCardCount(Number(event.target.value) as KitSupportedCardCount)
              }
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
            </select>
          </label>
          <label>
            <span>Card scale: {Math.round(cardScale * 100)}%</span>
            <input
              type="range"
              min={KIT_TEMPLATE_CARD_SCALE_MIN}
              max={KIT_TEMPLATE_CARD_SCALE_MAX}
              step={KIT_TEMPLATE_CARD_SCALE_STEP}
              value={cardScale}
              disabled={previewPattern?.cardScale === undefined}
              onChange={(event) => setCardScale(Number(event.target.value))}
            />
          </label>
          <label>
            <span>Background</span>
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={(event) => setBackgroundFile(event.target.files?.[0])}
            />
          </label>
        </div>
        <div className="tcvs-actions" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="tcvs-button tcvs-button-primary"
            disabled={creating}
            onClick={() => void createTemplate()}
          >
            {creating ? 'Creating...' : 'Create Draft Template'}
          </button>
        </div>
        <KitTemplatePreview
          backgroundUrl={backgroundPreviewUrl}
          slots={previewSlots}
          cardCount={cardCount}
        />
      </section>
      <section className="tcvs-card">
        {error !== undefined ? (
          <div className="tcvs-alert tcvs-alert-danger">
            {error}
            <button type="button" className="tcvs-button" onClick={() => void loadTemplates()}>
              Retry
            </button>
          </div>
        ) : null}
        <div className="tcvs-table-wrap">
          <table className="tcvs-table">
            <thead>
              <tr>
                <th>Template</th>
                <th>Cards</th>
                <th>Canvas</th>
                <th>Background</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}>Loading templates...</td>
                </tr>
              ) : null}
              {!loading && templates.length === 0 ? (
                <tr>
                  <td colSpan={6}>No templates found.</td>
                </tr>
              ) : null}
              {!loading
                ? templates.map((template) => (
                    <tr key={template.templateId}>
                      <td>
                        <strong>{template.name}</strong>
                        <br />
                        <span className="tcvs-mono">{template.templateId}</span>
                      </td>
                      <td>{template.cardCount}</td>
                      <td>
                        {template.canvasWidth} x {template.canvasHeight}
                      </td>
                      <td>
                        <KitTemplateBackgroundCell
                          template={template}
                          onView={() => setBackgroundPopoutTemplate(template)}
                        />
                      </td>
                      <td>{kitTemplateStatusLabel(template)}</td>
                      <td>
                        {template.source === 'operator' ? (
                          <div className="tcvs-actions">
                            <button
                              type="button"
                              className="tcvs-button"
                              disabled={
                                activatingTemplateId !== undefined ||
                                deletingTemplateId !== undefined ||
                                (template.status === 'ACTIVE' && template.isDefault)
                              }
                              onClick={() => void activateTemplate(template)}
                            >
                              {activatingTemplateId === template.templateId
                                ? 'Activating...'
                                : template.status === 'ACTIVE' && template.isDefault
                                  ? 'Default'
                                  : 'Make Default'}
                            </button>
                            <button
                              type="button"
                              className="tcvs-button tcvs-btn-danger"
                              disabled={
                                activatingTemplateId !== undefined ||
                                deletingTemplateId !== undefined ||
                                (template.status === 'ACTIVE' && template.isDefault)
                              }
                              onClick={() => void deleteTemplate(template)}
                            >
                              {deletingTemplateId === template.templateId
                                ? 'Deleting...'
                                : 'Delete'}
                            </button>
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
        </div>
      </section>
      <section className="tcvs-card tcvs-card-info" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Registry</h3>
        <div className="tcvs-table-wrap">
          <table className="tcvs-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Pattern</th>
                <th>Layout</th>
                <th>Scale</th>
                <th>Slots</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5}>Loading registry...</td>
                </tr>
              ) : null}
              {!loading
                ? templates.map((template) => (
                    <tr key={`${template.templateId}-registry`}>
                      <td>{template.source}</td>
                      <td className="tcvs-mono">{template.patternId}</td>
                      <td>{template.layout}</td>
                      <td>{kitTemplateCardScaleLabel(template)}</td>
                      <td>{template.slots.length}</td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
        </div>
      </section>
      {backgroundPopoutTemplate !== undefined ? (
        <KitTemplateBackgroundPopout
          template={backgroundPopoutTemplate}
          onClose={() => setBackgroundPopoutTemplate(undefined)}
        />
      ) : null}
    </div>
  );
}

function KitTemplateBackgroundCell({
  template,
  onView,
}: {
  template: KitTemplate;
  onView: () => void;
}): JSX.Element {
  return (
    <div className="tcvs-kit-template-background-cell">
      <button
        type="button"
        className="tcvs-kit-template-background-thumb"
        onClick={onView}
        aria-label={`View ${template.name} background`}
        title="View background"
        style={{ backgroundColor: kitTemplateBackgroundColor(template) }}
      >
        <KitTemplateBackgroundVisual template={template} compact />
      </button>
      <div>
        <span>{kitTemplateBackgroundLabel(template.background)}</span>
        <br />
        <button type="button" className="tcvs-link-button" onClick={onView}>
          View
        </button>
      </div>
    </div>
  );
}

function KitTemplateBackgroundVisual({
  template,
  compact = false,
}: {
  template: KitTemplate;
  compact?: boolean;
}): JSX.Element {
  const previewUrl = kitTemplateBackgroundPreviewUrl(template);
  if (previewUrl !== undefined) {
    return <img src={previewUrl} alt="" aria-hidden="true" />;
  }
  if (template.background.kind === 'designed') {
    return (
      <span
        className={
          compact
            ? 'tcvs-kit-template-background-designed'
            : 'tcvs-kit-template-background-designed tcvs-kit-template-background-designed-large'
        }
        aria-hidden="true"
      />
    );
  }
  return <span className="tcvs-kit-template-background-color" aria-hidden="true" />;
}

function KitTemplateBackgroundPopout({
  template,
  onClose,
}: {
  template: KitTemplate;
  onClose: () => void;
}): JSX.Element {
  return (
    <div
      className="tcvs-kit-template-background-popout"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kit-template-background-popout-title"
      onClick={onClose}
    >
      <div
        className="tcvs-kit-template-background-popout-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="tcvs-kit-template-background-popout-header">
          <div>
            <h3 id="kit-template-background-popout-title">{template.name}</h3>
            <span className="tcvs-muted">{kitTemplateBackgroundLabel(template.background)}</span>
          </div>
          <button
            type="button"
            className="tcvs-icon-button"
            onClick={onClose}
            aria-label="Close background preview"
            title="Close"
          >
            <span aria-hidden="true">X</span>
          </button>
        </header>
        <div
          className="tcvs-kit-template-background-popout-image"
          style={{ backgroundColor: kitTemplateBackgroundColor(template) }}
        >
          <KitTemplateBackgroundVisual template={template} />
        </div>
      </div>
    </div>
  );
}

const SAMPLE_CARD_URLS = [
  '/kitting-preview-samples/00.png',
  '/kitting-preview-samples/34.png',
  '/kitting-preview-samples/9.png',
  '/kitting-preview-samples/1.png',
  '/kitting-preview-samples/12.png',
] as const;

function KitTemplatePreview({
  backgroundUrl,
  slots,
  cardCount,
}: {
  backgroundUrl: string | undefined;
  slots: readonly KitPatternSlot[];
  cardCount: KitSupportedCardCount;
}): JSX.Element {
  const canPreview = cardCount === 5 && backgroundUrl !== undefined && slots.length >= 5;
  return (
    <div className="tcvs-kit-template-preview-shell">
      <div className="tcvs-kit-template-preview-toolbar">
        <strong>Preview</strong>
        <span className="tcvs-muted">
          {canPreview
            ? 'Sample cards on selected background'
            : 'Choose a 5-card background to preview'}
        </span>
      </div>
      <div className="tcvs-kit-template-preview">
        {canPreview ? (
          <>
            <img
              src={backgroundUrl}
              alt=""
              className="tcvs-kit-template-preview-bg"
              aria-hidden="true"
            />
            {SAMPLE_CARD_URLS.map((src, index) => {
              const slot = slots[index]!;
              const borderWidth = slot.borderWidth ?? 0;
              return (
                <div
                  key={src}
                  className={
                    slot.shadow === true
                      ? 'tcvs-kit-template-preview-card tcvs-kit-template-preview-card-shadow'
                      : 'tcvs-kit-template-preview-card'
                  }
                  style={{
                    left: `${((slot.x - borderWidth) / 1600) * 100}%`,
                    top: `${((slot.y - borderWidth) / 1600) * 100}%`,
                    width: `${((slot.width + borderWidth * 2) / 1600) * 100}%`,
                    height: `${((slot.height + borderWidth * 2) / 1600) * 100}%`,
                    transform: `rotate(${slot.rotateDegrees ?? 0}deg)`,
                    zIndex: slot.zIndex ?? index,
                    borderWidth: `${Math.max(1, Math.round(borderWidth * 0.325))}px`,
                    borderColor: slot.borderColor ?? 'transparent',
                  }}
                >
                  <img src={src} alt="" aria-hidden="true" />
                </div>
              );
            })}
          </>
        ) : (
          <div className="tcvs-kit-template-preview-empty">No preview</div>
        )}
      </div>
    </div>
  );
}

function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to read background image dimensions'));
    };
    img.src = url;
  });
}
